import { useMemo } from 'react';

import { useGraphQLQuery } from '@betterment/graphql';
import { Box, Card, H3, Icon, Separator, Text } from '@betterment/samba-react';
import { CardLoader, SambaAutoLink } from '@betterment/shared-components';
import { iconDisplayForSecurity } from '@betterment/shared-components/src/utils/accountSecurityHelpers';
import { useNavigate } from 'react-router-dom';

import { PerformanceChartSection } from './components/price-performance/PerformanceChartSection';
import { DirectTradingAccountSecurityDocument } from './graphql/directTradingAccountSecurity.generated';

import { urlBuilder } from '../../../Shared/urlBuilder';
import { Props } from '../../AccountSecurityPage';

export const OverviewCard: React.FC<Props> = ({ accountId, securityId }) => {
  const currentDate = useMemo(() => new Date(), []);
  const { data } = useGraphQLQuery(DirectTradingAccountSecurityDocument, {
    variables: { accountId, securityId },
    throwOnError: true,
  });
  const navigate = useNavigate();

  const navigateToPreviousPage = () => {
    void navigate(-1);
  };

  if (!data) return <CardLoader />;
  if (!data.account || data.account.__typename !== 'DirectTradingAccount') {
    navigateToPreviousPage();
    return null;
  }

  const { account } = data;
  const { financialSecurity } = account;

  return (
    <Card css={{ display: 'grid', gap: 'space.300', padding: 'space.300' }}>
      <Card.Header>
        <SambaAutoLink to={urlBuilder.appAccount.absolute({ accountId: account.id })}>
          <Box css={{ display: 'flex', alignItems: 'center', color: 'text' }}>
            <Icon name="CaretLeft" size="small" css={{ mr: 'space.100' }} />
            <Text>{account.nameOverride || account.accountDescription}</Text>
          </Box>
        </SambaAutoLink>
      </Card.Header>
      <Card.Content css={{ gap: 'space.300', display: 'grid' }}>
        <Box css={{ display: 'flex', verticalAlign: 'center', alignItems: 'center', gap: 'space.200' }}>
          <Box css={{ transform: 'scale(1.33)' }}>
            {iconDisplayForSecurity({
              symbol: financialSecurity.symbol,
              logo:
                financialSecurity.__typename === 'FinancialSecurityStock'
                  ? financialSecurity.companyProfile?.logo
                  : null,
            })}
          </Box>
          <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box css={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <H3 variant="title300">{financialSecurity.name}</H3>
              <Text color="text.weak" variant="text100">
                {financialSecurity.symbol}
              </Text>
            </Box>
          </Box>
        </Box>

        <Separator />

        <PerformanceChartSection accountId={accountId} securityId={securityId} currentDate={currentDate} />
      </Card.Content>
    </Card>
  );
};
